export const translations = {
  en: {
    beforeAfter: {
      overline: "The editing craft",
      title: "Before & After.",
      subtitle:
        "Drag the handle to see the difference — same moment, refined tone, timeless colour, real skin.",
      before: "Before",
      after: "After",
      hint: "Drag the handle",
    },
    nav: {
      home: "Home",
      about: "About",
      packages: "Packages",
      gallery: "Gallery",
      experience: "Experience",
      booking: "Book",
      cta: "Book Session",
    },
    hero: {
      overline: "Family · Couples · Elopements",
      title1: "Beautiful Family Memories",
      title2: "That Last Forever",
      subtitle:
        "Relaxed, natural and emotional photography sessions for families, couples and the moments that matter most.",
      cta1: "Book Your Session",
      cta2: "View Packages",
      scroll: "Scroll to discover",
    },
    trust: {
      overline: "Loved by families",
      title: "Real moments. Real reviews.",
      subtitle:
        "What parents, couples and travellers say after their session.",
      shareCta: "Share your experience",
      formTitle: "Tell your story.",
      formSubtitle:
        "Your review helps future families find us. We review each one before it goes live.",
      fields: {
        name: "Your name",
        rating: "Your rating",
        message: "Your review",
        sessionType: "Session (optional)",
        location: "Location (optional)",
      },
      placeholders: {
        name: "First name + last initial",
        message: "Share what the session felt like…",
        session: "Family, Couple, VIP…",
        location: "Chania, Elafonissi…",
      },
      submit: "Submit review",
      sending: "Sending…",
      successTitle: "Thank you.",
      successBody:
        "Your review has been received and is awaiting a quick approval. It will appear here shortly.",
      sendAnother: "Send another",
      errorGeneric: "Something went wrong. Please try again later.",
      cancel: "Close",
      reviews: [
        {
          quote:
            "She made us feel instantly relaxed. Our children loved every moment of the session — these are photos we will cherish forever.",
          name: "Sofia M.",
          tag: "Family Session · Chania, Crete",
        },
        {
          quote:
            "The most natural, fun and beautiful experience. Every photo tells the story of our weekend in Crete.",
          name: "Daniel & Léa",
          tag: "Couple · Elafonissi, Crete",
        },
        {
          quote:
            "Warm, kind and so patient with our toddler. The photos are absolutely stunning and feel completely us.",
          name: "Anna K.",
          tag: "Motherhood · Heraklion, Crete",
        },
      ],
    },
    about: {
      overline: "About the photographer",
      title: "Soft light, real laughter, slow moments.",
      paragraph1:
        "I believe the most beautiful photographs are not posed — they are felt. My sessions are warm, unhurried and built around your family the way it really is.",
      paragraph2:
        "Children are free to be children. Couples are free to forget the camera. My only job is to gently capture the love that already lives between you.",
      paragraph3:
        "Every gallery I deliver is meant to be looked at twenty years from now and still feel like home.",
      stats: [
        { value: "9+", label: "Years of experience" },
        { value: "400+", label: "Families & couples" },
        { value: "5.0", label: "Average rating" },
      ],
    },
    whyChoose: {
      overline: "Why choose Click Photography",
      title: "Photography that tells your story.",
      subtitle:
        "Every image is created with precision, creativity and care — designed not just to look beautiful, but to feel like you. Real emotion, natural light and moments you'll come back to for years.",
      items: [
        {
          title: "Professional Quality",
          desc: "Premium gear, refined craft and an eye trained on the details that most people miss.",
        },
        {
          title: "Natural & Authentic Editing",
          desc: "True-to-skin tones, soft contrast and timeless colour — never over-filtered.",
        },
        {
          title: "Stress-Free Experience",
          desc: "Warm, calm direction that puts kids, parents and partners completely at ease.",
        },
      ],
    },
    packages: {
      overline: "Investment",
      title: "Sessions designed around real life.",
      subtitle:
        "Transparent pricing. No hidden fees. Every gallery is hand-edited with care.",
      items: [
        {
          name: "Basic Photoshoot",
          duration: "45 minutes",
          photos: "25 edited photos",
          price: "150€",
          features: [
            "One location",
            "Natural light photography",
            "Online private gallery",
            "Personal use license",
          ],
        },
        {
          name: "Family / Lifestyle",
          duration: "1 – 1.5 hours",
          photos: "30 – 50 edited photos",
          price: "180€ – 250€",
          features: [
            "Up to 6 people",
            "One or two locations",
            "Lifestyle direction",
            "Online private gallery",
          ],
          featured: true,
          tag: "Most loved",
        },
        {
          name: "VIP Experience",
          duration: "2 hours",
          photos: "50+ edited photos",
          price: "250€ – 280€",
          features: [
            "Multi-location session",
            "Outfit guidance",
            "Priority editing",
            "Cinematic story gallery",
          ],
        },
        {
          name: "Proposal / Engagement",
          duration: "Tailored",
          photos: "50+ edited photos",
          price: "280€ – 350€",
          features: [
            "Discreet documentary style",
            "Coordination with planner",
            "Same-day teaser preview",
            "Romantic golden-hour focus",
          ],
        },
      ],
      addon: {
        title: "Drone Photography",
        desc: "Add cinematic aerial coverage to any session.",
        price: "+50€ – 80€",
      },
    },
    offer: {
      overline: "Limited time",
      title: "6 Professional Photos",
      detail: "📸 Up to 20 minutes of shooting",
      subtitle:
        "A short, joyful mini-session perfect for travellers, holidaymakers and last-minute family memories.",
      bonus: "Free Photo Keychain or Photo Magnet included with every booking.",
      cta: "Book This Offer",
    },
    gallery: {
      overline: "Stories in light",
      title: "A gallery of real moments.",
      subtitle:
        "Families, couples, motherhood and golden-hour memories from recent sessions.",
      categories: ["All", "Family", "Couples", "Motherhood", "Children", "Sunset"],
    },
    experience: {
      overline: "What your session feels like",
      title: "No awkward posing. Just real you.",
      subtitle:
        "Every session is built around comfort, warmth and the rhythm of your family.",
      values: [
        { title: "Relaxed", desc: "No stiff posing. We move, talk and play." },
        { title: "Natural", desc: "Soft light, real expressions, honest moments." },
        { title: "Great with children", desc: "Patient, playful and never rushed." },
        { title: "Warm & welcoming", desc: "You feel met from the very first hello." },
        { title: "Stress-free", desc: "Outfit and location guidance ahead of time." },
        { title: "Memory-driven", desc: "Galleries you'll still love in twenty years." },
      ],
    },
    booking: {
      overline: "Reserve your date",
      title: "Let's create something to cherish.",
      subtitle:
        "Send a message on WhatsApp, scan the QR or open the booking form. I personally reply to every enquiry.",
      whatsappLabel: "Message on WhatsApp",
      formLabel: "Open the booking form",
      qrLabel: "Or scan with your camera",
      contact: "+30 699 366 50 29",
    },
    bookingForm: {
      overline: "Or use the quick form",
      title: "Tell me about your session.",
      subtitle:
        "Fill in a few details and I'll get back to you personally within 24 hours.",
      fields: {
        name: "Full name",
        email: "Email address",
        phone: "Phone / WhatsApp",
        sessionType: "Session type",
        date: "Preferred date",
        people: "Number of people",
        location: "Location",
        message: "Anything else I should know?",
      },
      placeholders: {
        name: "Your full name",
        email: "you@email.com",
        phone: "+30 …",
        location: "Chania, Elafonissi, hotel name…",
        message: "Time of day you'd love, mood you want, questions…",
      },
      sessionOptions: [
        "Basic Photoshoot — 150€",
        "Family / Lifestyle — 180–250€",
        "VIP Experience — 250–280€",
        "Proposal / Engagement — 280–350€",
        "6 Photos Special Offer — 49€",
        "Not sure yet",
      ],
      consent:
        "I agree that Click Photography may store and process my submitted information for the purpose of responding to my inquiry.",
      submit: "Send booking request",
      sending: "Sending…",
      successTitle: "Thank you — your request is on its way.",
      successBody:
        "I'll reply personally within 24 hours. Want to reach me faster? Continue on WhatsApp with your details already filled in.",
      openWhatsapp: "Continue on WhatsApp",
      sendAnother: "Send another",
      errorGeneric:
        "Something went wrong sending your request. Please try WhatsApp or email instead.",
      required: "Required",
    },
    footer: {
      tagline: "Natural family photography · Couples · Elopements",
      rights: "All rights reserved.",
      links: { instagram: "Instagram", whatsapp: "WhatsApp", booking: "Booking" },
    },
    cookies: {
      title: "We use cookies",
      body: "We use essential and analytics cookies to improve your browsing experience and understand how visitors use our site. You can accept all, reject non-essential, or read more in our Cookie Policy.",
      accept: "Accept all",
      reject: "Reject non-essential",
      learnMore: "Cookie Policy",
    },
  },
  gr: {
    beforeAfter: {
      overline: "Η τέχνη της επεξεργασίας",
      title: "Πριν & Μετά.",
      subtitle:
        "Σύρετε τη λαβή για να δείτε τη διαφορά — ίδια στιγμή, εκλεπτυσμένος τόνος, διαχρονικό χρώμα, αληθινό δέρμα.",
      before: "Πριν",
      after: "Μετά",
      hint: "Σύρετε τη λαβή",
    },
    nav: {
      home: "Αρχική",
      about: "Σχετικά",
      packages: "Πακέτα",
      gallery: "Γκαλερί",
      experience: "Εμπειρία",
      booking: "Κράτηση",
      cta: "Κλείσε Φωτογράφιση",
    },
    hero: {
      overline: "Οικογένεια · Ζευγάρια · Elopements",
      title1: "Όμορφες Οικογενειακές Στιγμές",
      title2: "Που Διαρκούν για Πάντα",
      subtitle:
        "Χαλαρές, φυσικές και συναισθηματικές φωτογραφίσεις για οικογένειες, ζευγάρια και τις στιγμές που μετράνε.",
      cta1: "Κλείσε Φωτογράφιση",
      cta2: "Δες τα Πακέτα",
      scroll: "Κύλισε για να ανακαλύψεις",
    },
    trust: {
      overline: "Αγαπημένο από οικογένειες",
      title: "Αληθινές στιγμές. Αληθινές κριτικές.",
      subtitle:
        "Τι λένε γονείς, ζευγάρια και ταξιδιώτες μετά τη φωτογράφισή τους.",
      shareCta: "Μοιραστείτε την εμπειρία σας",
      formTitle: "Πείτε την ιστορία σας.",
      formSubtitle:
        "Η κριτική σας βοηθά μελλοντικές οικογένειες να μας βρουν. Ελέγχουμε κάθε κριτική πριν δημοσιευτεί.",
      fields: {
        name: "Το όνομά σας",
        rating: "Βαθμολογία",
        message: "Η κριτική σας",
        sessionType: "Φωτογράφιση (προαιρετικά)",
        location: "Τοποθεσία (προαιρετικά)",
      },
      placeholders: {
        name: "Όνομα + αρχικό επωνύμου",
        message: "Πείτε μας πώς νιώσατε τη φωτογράφιση…",
        session: "Οικογενειακή, Ζευγάρι, VIP…",
        location: "Χανιά, Ελαφονήσι…",
      },
      submit: "Υποβολή κριτικής",
      sending: "Γίνεται αποστολή…",
      successTitle: "Ευχαριστώ.",
      successBody:
        "Η κριτική σας παραλήφθηκε και είναι σε αναμονή γρήγορης έγκρισης. Θα εμφανιστεί σύντομα.",
      sendAnother: "Νέα κριτική",
      errorGeneric: "Κάτι πήγε στραβά. Δοκιμάστε ξανά σε λίγο.",
      cancel: "Κλείσιμο",
      reviews: [
        {
          quote:
            "Μας έκανε να νιώσουμε αμέσως άνετα. Τα παιδιά μας λάτρεψαν κάθε στιγμή — φωτογραφίες που θα κρατήσουμε για πάντα.",
          name: "Σοφία Μ.",
          tag: "Οικογενειακή · Χανιά, Κρήτη",
        },
        {
          quote:
            "Η πιο φυσική, διασκεδαστική και όμορφη εμπειρία. Κάθε φωτογραφία αφηγείται το σαββατοκύριακό μας στην Κρήτη.",
          name: "Daniel & Léa",
          tag: "Ζευγάρι · Ελαφονήσι, Κρήτη",
        },
        {
          quote:
            "Ζεστή, ευγενική και τόσο υπομονετική με τον γιο μου. Οι φωτογραφίες είναι εκπληκτικές και πραγματικά εμείς.",
          name: "Άννα Κ.",
          tag: "Μητρότητα · Ηράκλειο, Κρήτη",
        },
      ],
    },
    about: {
      overline: "Σχετικά με τη φωτογράφο",
      title: "Απαλό φως, αληθινό γέλιο, αργές στιγμές.",
      paragraph1:
        "Πιστεύω ότι οι πιο όμορφες φωτογραφίες δεν είναι ποζαρισμένες — είναι αυτές που νιώθουμε. Οι φωτογραφίσεις μου είναι ζεστές και χτισμένες γύρω από την οικογένειά σας όπως πραγματικά είναι.",
      paragraph2:
        "Τα παιδιά είναι ελεύθερα να είναι παιδιά. Τα ζευγάρια ξεχνούν την κάμερα. Η δουλειά μου είναι απλά να αποτυπώσω την αγάπη που ήδη υπάρχει.",
      paragraph3:
        "Κάθε γκαλερί είναι φτιαγμένη για να την κοιτάτε σε είκοσι χρόνια και να νιώθετε σαν σπίτι.",
      stats: [
        { value: "9+", label: "Χρόνια εμπειρίας" },
        { value: "400+", label: "Οικογένειες & ζευγάρια" },
        { value: "5.0", label: "Μέση βαθμολογία" },
      ],
    },
    whyChoose: {
      overline: "Γιατί να επιλέξετε την Click Photography",
      title: "Φωτογραφία που αφηγείται την ιστορία σας.",
      subtitle:
        "Κάθε εικόνα δημιουργείται με ακρίβεια, δημιουργικότητα και φροντίδα — όχι απλώς για να είναι όμορφη, αλλά για να σας μοιάζει. Αληθινό συναίσθημα, φυσικό φως και στιγμές που θα ξαναζείτε για χρόνια.",
      items: [
        {
          title: "Επαγγελματική Ποιότητα",
          desc: "Premium εξοπλισμός, εκλεπτυσμένη τέχνη και ματιά που πιάνει αυτά που οι άλλοι αφήνουν να χαθούν.",
        },
        {
          title: "Φυσική & Αυθεντική Επεξεργασία",
          desc: "Πραγματικοί τόνοι δέρματος, απαλή αντίθεση και διαχρονικό χρώμα — ποτέ over-filtered.",
        },
        {
          title: "Χωρίς Άγχος",
          desc: "Ζεστή, ήρεμη καθοδήγηση που βάζει παιδιά, γονείς και συντρόφους εντελώς στα νερά τους.",
        },
      ],
    },
    packages: {
      overline: "Επένδυση",
      title: "Φωτογραφίσεις για την αληθινή ζωή.",
      subtitle:
        "Διαφανείς τιμές. Χωρίς κρυφά κόστη. Κάθε γκαλερί επιμελείται με φροντίδα.",
      items: [
        {
          name: "Βασική Φωτογράφιση",
          duration: "45 λεπτά",
          photos: "25 επεξεργασμένες φωτογραφίες",
          price: "150€",
          features: [
            "Μία τοποθεσία",
            "Φυσικό φως",
            "Online ιδιωτική γκαλερί",
            "Άδεια προσωπικής χρήσης",
          ],
        },
        {
          name: "Οικογενειακή / Lifestyle",
          duration: "1 – 1.5 ώρες",
          photos: "30 – 50 επεξεργασμένες",
          price: "180€ – 250€",
          features: [
            "Έως 6 άτομα",
            "Μία ή δύο τοποθεσίες",
            "Lifestyle καθοδήγηση",
            "Online ιδιωτική γκαλερί",
          ],
          featured: true,
          tag: "Πιο αγαπημένο",
        },
        {
          name: "VIP Εμπειρία",
          duration: "2 ώρες",
          photos: "50+ επεξεργασμένες",
          price: "250€ – 280€",
          features: [
            "Πολλαπλές τοποθεσίες",
            "Συμβουλές για ντύσιμο",
            "Επεξεργασία προτεραιότητας",
            "Cinematic γκαλερί",
          ],
        },
        {
          name: "Πρόταση / Αρραβώνας",
          duration: "Προσαρμοσμένη",
          photos: "50+ επεξεργασμένες",
          price: "280€ – 350€",
          features: [
            "Διακριτικό ντοκιμαντέρ στυλ",
            "Συντονισμός με τον planner",
            "Teaser ίδιας ημέρας",
            "Ρομαντικό golden hour",
          ],
        },
      ],
      addon: {
        title: "Drone Photography",
        desc: "Προσθέστε εναέρια λήψη σε οποιαδήποτε φωτογράφιση.",
        price: "+50€ – 80€",
      },
    },
    offer: {
      overline: "Περιορισμένη προσφορά",
      title: "6 Επαγγελματικές Φωτογραφίες",
      detail: "📸 Έως 20 λεπτά φωτογράφισης",
      subtitle:
        "Μια σύντομη χαρούμενη φωτογράφιση, ιδανική για ταξιδιώτες και οικογένειες σε διακοπές.",
      bonus: "Δωρεάν Photo Keychain ή Photo Magnet με κάθε κράτηση.",
      cta: "Κλείσε την Προσφορά",
    },
    gallery: {
      overline: "Ιστορίες στο φως",
      title: "Μια γκαλερί από αληθινές στιγμές.",
      subtitle:
        "Οικογένειες, ζευγάρια, μητρότητα και golden-hour αναμνήσεις.",
      categories: ["Όλα", "Οικογένεια", "Ζευγάρια", "Μητρότητα", "Παιδιά", "Sunset"],
    },
    experience: {
      overline: "Πώς νιώθει η φωτογράφισή σου",
      title: "Χωρίς αμήχανες πόζες. Μόνο εσύ.",
      subtitle:
        "Κάθε φωτογράφιση χτίζεται γύρω από την άνεση και τον ρυθμό σου.",
      values: [
        { title: "Χαλαρά", desc: "Χωρίς ποζάρισμα. Κινούμαστε, μιλάμε, παίζουμε." },
        { title: "Φυσικά", desc: "Απαλό φως, αληθινές εκφράσεις." },
        { title: "Άνετα με παιδιά", desc: "Υπομονετικά, χωρίς βιασύνη." },
        { title: "Ζεστή υποδοχή", desc: "Νιώθεις άνετα από το πρώτο γεια." },
        { title: "Χωρίς άγχος", desc: "Καθοδήγηση για ντύσιμο και τοποθεσία." },
        { title: "Με ψυχή", desc: "Γκαλερί που θα αγαπάς για χρόνια." },
      ],
    },
    booking: {
      overline: "Κράτησε την ημερομηνία σου",
      title: "Ας δημιουργήσουμε κάτι αξέχαστο.",
      subtitle:
        "Στείλε μήνυμα στο WhatsApp, σκάναρε το QR ή άνοιξε τη φόρμα κράτησης.",
      whatsappLabel: "Στείλε WhatsApp",
      formLabel: "Φόρμα κράτησης",
      qrLabel: "Ή σκάναρε με την κάμερα",
      contact: "+30 699 366 50 29",
    },
    bookingForm: {
      overline: "Ή χρησιμοποιήστε τη γρήγορη φόρμα",
      title: "Πες μου για τη φωτογράφισή σου.",
      subtitle:
        "Συμπλήρωσε λίγα στοιχεία και θα σου απαντήσω προσωπικά μέσα σε 24 ώρες.",
      fields: {
        name: "Ονοματεπώνυμο",
        email: "Email",
        phone: "Τηλέφωνο / WhatsApp",
        sessionType: "Είδος φωτογράφισης",
        date: "Προτιμώμενη ημερομηνία",
        people: "Αριθμός ατόμων",
        location: "Τοποθεσία",
        message: "Κάτι άλλο που πρέπει να ξέρω;",
      },
      placeholders: {
        name: "Το ονοματεπώνυμό σας",
        email: "esas@email.com",
        phone: "+30 …",
        location: "Χανιά, Ελαφονήσι, όνομα ξενοδοχείου…",
        message: "Ώρα που προτιμάς, mood που θέλεις, ερωτήσεις…",
      },
      sessionOptions: [
        "Βασική Φωτογράφιση — 150€",
        "Οικογενειακή / Lifestyle — 180–250€",
        "VIP Εμπειρία — 250–280€",
        "Πρόταση / Αρραβώνας — 280–350€",
        "6 Φωτογραφίες Προσφορά — 49€",
        "Δεν είμαι σίγουρη/ος ακόμα",
      ],
      consent:
        "Συμφωνώ ότι η Click Photography μπορεί να αποθηκεύσει και να επεξεργαστεί τα στοιχεία μου με σκοπό την απάντηση στο αίτημά μου.",
      submit: "Αποστολή αιτήματος",
      sending: "Γίνεται αποστολή…",
      successTitle: "Ευχαριστώ — το αίτημά σας εστάλη.",
      successBody:
        "Θα σας απαντήσω προσωπικά μέσα σε 24 ώρες. Θέλετε πιο γρήγορα; Συνεχίστε στο WhatsApp με τα στοιχεία σας ήδη συμπληρωμένα.",
      openWhatsapp: "Συνέχεια στο WhatsApp",
      sendAnother: "Νέα αποστολή",
      errorGeneric:
        "Κάτι πήγε στραβά. Δοκιμάστε WhatsApp ή email.",
      required: "Απαιτείται",
    },
    footer: {
      tagline: "Φυσική οικογενειακή φωτογραφία · Ζευγάρια · Elopements",
      rights: "Με επιφύλαξη παντός δικαιώματος.",
      links: { instagram: "Instagram", whatsapp: "WhatsApp", booking: "Κράτηση" },
    },
    cookies: {
      title: "Χρησιμοποιούμε cookies",
      body: "Χρησιμοποιούμε απαραίτητα και analytics cookies για να βελτιώσουμε την εμπειρία σας και να κατανοήσουμε πώς χρησιμοποιείτε τον ιστότοπο. Μπορείτε να αποδεχτείτε όλα, να απορρίψετε τα μη απαραίτητα ή να διαβάσετε περισσότερα στην Πολιτική Cookies.",
      accept: "Αποδοχή όλων",
      reject: "Απόρριψη μη απαραίτητων",
      learnMore: "Πολιτική Cookies",
    },
  },
};
